// src/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth as authAPI, oauth as oauthAPI, hasSessionHint, setSessionHint, setCsrfToken } from './api';
import { bindSession } from './lib/activityEvents';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);
  // Rehydrate the session on mount. Users stay signed in until they log out or
  // the 30-day refresh session ends; api.js swaps an expired 24h access cookie
  // for a fresh one, so there is no idle auto-logout.
  useEffect(() => {
    if (!hasSessionHint()) {
      setLoading(false);
      return;
    }
    authAPI.me()
      .then(u => setUser(u))
      .catch((err) => {
        // Only a rejected session signs the user out; a cold start or network
        // blip leaves the hint so the next load tries again.
        if (err && err.status === 401) {
          setSessionHint(false);
          setCsrfToken(null);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const _setSession = (user) => {
    setSessionHint(true);
    setUser(user);
    // Bind this browser's anonymous activity (by gb_session_id) to the fresh
    // account. Fire-and-forget: bindSession swallows its own errors.
    bindSession();
    return user;
  };

  const login       = async (email, password)              => _setSession((await authAPI.login(email, password)).user);
  const signup      = async (name, email, pass, username)  => authAPI.signup(name, email, pass, username); // no session — verification required
  const googleLogin = async (token, type = 'id_token')     => _setSession((await oauthAPI.google(token, type)).user);
  const updateUser = (patch) => setUser(u => (u ? { ...u, ...patch } : u));
  const logout = async () => {
    try { await authAPI.logout(); } catch (e) { /* ignore */ }
    setSessionHint(false);
    setCsrfToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, googleLogin, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
